import { Entity, generateEntityId } from './entity';
import type { CollisionMask } from '../sim/collisionMask';
import { WA_PHYSICS, type Vector2 } from '../core/constants';

export type WormState =
  | 'idle'
  | 'walking'
  | 'jumping'
  | 'backflipping'
  | 'falling'
  | 'aiming'
  | 'firing'
  | 'drowning'
  | 'dead';
export type FacingDirection = 'left' | 'right';

const PARACHUTE_FALL_SPEED = 50;

export class Worm implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2;
  active = true;

  name: string;
  teamId: string;
  health: number;
  maxHealth: number = WA_PHYSICS.WORM.MAX_HEALTH;
  pendingDamage = 0;
  state: WormState = 'idle';
  facing: FacingDirection = 'right';
  aimAngle = 45;
  /** Allowed aim range in degrees (mode-configurable). */
  aimMin = -90;
  aimMax = 90;
  /** GunBound slope-relative aim: the whole aim span rotates with the ground
   *  the tank stands on. This is the slope tilt (degrees) under the worm,
   *  facing-signed (uphill = positive), added to the player's relative
   *  `aimAngle` to get the world launch angle. 0 on flat ground / when the
   *  mode rule is off. Live while grounded, frozen at fire time. */
  groundTiltDeg = 0;
  /** Raw surface slope under the worm (degrees, positive when the ground rises
   *  to the right) - FACING-INDEPENDENT, used to rotate the worm sprite so it
   *  sits on the slope. 0 on flat ground / when the mode rule is off. */
  groundSlopeDeg = 0;
  /** When false (classic), aim is absolute-vertical and groundTiltDeg stays 0. */
  aimRelativeToGround = false;
  /**
   * This worm's last selected weapon, restored when its turn begins
   * (per-worm weapon memory). Authority-only; not serialized to clients.
   */
  lastWeaponId?: string;
  isGrounded = false;
  hasParachute = true;
  parachuteOpen = false;
  isThrown = false;
  drowningTimer = 0;
  immuneToFallDamage = false;
  drowningSplashPlayed = false;
  skipGraveOnDeath = false;
  kamikazing = false;
  kamikazeDirection = { x: 0, y: 0 };
  kamikazeDistance = 0;
  jetpackActive = false;
  jetpackFuel = 0;

  onFallDamage?: (damage: number) => void;
  onJetpackDeactivate?: () => void;
  onDrown?: () => void;
  onDamage?: (damage: number) => void;
  onDie?: (position: { x: number; y: number }) => void;

  private readonly width = WA_PHYSICS.WORM.WIDTH;
  private readonly height = WA_PHYSICS.WORM.HEIGHT;

  constructor(
    name: string,
    teamId: string,
    x: number,
    y: number,
    initialHealth: number = WA_PHYSICS.WORM.MAX_HEALTH,
  ) {
    this.id = generateEntityId('worm');
    this.name = name;
    this.teamId = teamId;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.maxHealth = initialHealth;
    this.health = initialHealth;
  }

  setAimLimits(min: number, max: number): void {
    this.aimMin = Math.max(-90, Math.round(min));
    this.aimMax = Math.min(90, Math.round(max));
    this.setAimAngle(this.aimAngle);
  }

  update(
    deltaTime: number,
    wind: number = 0,
    _terrain?: CollisionMask,
    terrainFriction?: number,
  ): void {
    if (this.state === 'dead') return;

    if (this.state === 'drowning') {
      this.drowningTimer -= deltaTime;
      this.velocity.x *= 0.95;
      this.velocity.y = 30;
      this.position.y += this.velocity.y * deltaTime;

      if (this.drowningTimer <= 0) {
        this.onDrown?.();
        this.die();
      }
      return;
    }

    if (this.kamikazing) {
      const kamiSpeed = 400;
      this.velocity.x = this.kamikazeDirection.x * kamiSpeed;
      this.velocity.y = this.kamikazeDirection.y * kamiSpeed;
      const moveX = this.velocity.x * deltaTime;
      const moveY = this.velocity.y * deltaTime;
      this.position.x += moveX;
      this.position.y += moveY;
      this.kamikazeDistance += Math.sqrt(moveX * moveX + moveY * moveY);
      return;
    }

    if (!this.isGrounded) {
      if (this.parachuteOpen) {
        this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime * 0.1;
        if (this.velocity.y < 0) {
          this.velocity.y *= 0.9;
        }
        this.velocity.y = Math.min(this.velocity.y, PARACHUTE_FALL_SPEED);
        this.velocity.x += wind * deltaTime * 15;
        this.velocity.x *= 0.98;
      } else {
        this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      }
    } else {
      if (this.parachuteOpen) {
        this.parachuteOpen = false;
      }
    }

    const maxVelocity = 800;
    this.velocity.x = Math.max(-maxVelocity, Math.min(maxVelocity, this.velocity.x));
    this.velocity.y = Math.max(-maxVelocity, Math.min(maxVelocity, this.velocity.y));

    const moveX = this.velocity.x * deltaTime;
    const moveY = this.velocity.y * deltaTime;

    const maxStep = 6;
    if (_terrain && (Math.abs(moveX) > maxStep || Math.abs(moveY) > maxStep)) {
      const steps = Math.ceil(Math.max(Math.abs(moveX), Math.abs(moveY)) / maxStep);
      const stepX = moveX / steps;
      const stepY = moveY / steps;

      for (let i = 0; i < steps; i++) {
        const nextX = this.position.x + stepX;
        const nextY = this.position.y + stepY;

        const collidesNext = this.collidesWithTerrain(nextX, nextY, _terrain, 'narrow');

        if (collidesNext) {
          const collidesX = this.collidesWithTerrain(nextX, this.position.y, _terrain, 'narrow');
          const collidesY = this.collidesWithTerrain(this.position.x, nextY, _terrain, 'narrow');

          if (collidesX && collidesY) {
            this.velocity.x = 0;
            this.velocity.y = 0;
            break;
          } else if (collidesX) {
            this.velocity.x = 0;
            this.position.y = nextY;
          } else if (collidesY) {
            this.velocity.y = 0;
            this.position.x = nextX;
          } else {
            this.position.x = nextX;
            this.position.y = nextY;
          }
          break;
        }
        this.position.x = nextX;
        this.position.y = nextY;
      }
    } else {
      this.position.x += moveX;
      this.position.y += moveY;
    }

    if (this.isGrounded) {
      const baseFriction = terrainFriction ?? WA_PHYSICS.WORM.FRICTION;
      const friction = this.isThrown
        ? Math.max(baseFriction, WA_PHYSICS.WORM.THROWN_FRICTION)
        : baseFriction;
      this.velocity.x *= friction;
      if (Math.abs(this.velocity.x) < 0.1) {
        this.velocity.x = 0;
        this.isThrown = false;
      }
    }
  }

  toggleParachute(): void {
    if (!this.isGrounded && this.hasParachute) {
      this.parachuteOpen = !this.parachuteOpen;
    }
  }

  closeParachute(): void {
    this.parachuteOpen = false;
  }

  move(direction: FacingDirection, terrain: CollisionMask, otherWorms?: Worm[]): boolean {
    if (this.state === 'dead' || this.state === 'firing') return false;
    if (!this.isGrounded) return false;

    this.facing = direction;
    this.state = 'walking';

    const speed = WA_PHYSICS.WORM.WALK_SPEED / WA_PHYSICS.TICK_RATE;
    const dx = direction === 'left' ? -speed : speed;

    for (let climbY = 0; climbY <= WA_PHYSICS.WORM.MAX_CLIMB; climbY++) {
      const newX = this.position.x + dx;
      const newY = this.position.y - climbY;

      // Skip the feet points: standing on a slope the uphill foot is
      // naturally embedded in the surface, which would force the climb to
      // also overcome the embedding and block walkable slopes. The ground
      // snap below re-seats the feet; head/torso points still block walls.
      if (!this.collidesWithTerrain(newX, newY, terrain, 'skipFeet')) {
        if (otherWorms && this.collidesWithWorms(newX, newY, otherWorms)) {
          this.state = 'idle';
          return false;
        }

        this.position.x = newX;
        this.position.y = newY;

        // Seat the feet on the surface: first pop up out of any embedding
        // (walking uphill), then drop down to the ground (walking downhill).
        let seatY = newY;
        for (
          let up = 0;
          up < WA_PHYSICS.WORM.MAX_CLIMB &&
          terrain.isSolid(newX + this.width / 2, seatY + this.height - 1);
          up++
        ) {
          seatY -= 1;
        }

        let foundGround = false;
        for (let dropY = 0; dropY <= WA_PHYSICS.WORM.MAX_CLIMB + 2; dropY++) {
          const testY = seatY + dropY;
          const feetY = testY + this.height;
          if (terrain.isSolid(newX + this.width / 2, feetY)) {
            this.position.y = testY;
            foundGround = true;
            break;
          }
        }

        if (!foundGround) {
          this.isGrounded = false;
          this.state = 'falling';
          // Carry the walk momentum off the ledge so the worm arcs forward and
          // clears the edge corner, instead of dropping straight down onto it
          // and re-grounding every tick (the "stuck on the edge" bug).
          this.velocity.x = dx * WA_PHYSICS.TICK_RATE;
        }
        return true;
      }
    }

    const edgeX = this.position.x + dx;
    const edgeY = this.position.y;
    const feetY = edgeY + this.height;
    const centerX = edgeX + this.width / 2;

    const hasGroundAhead = terrain.isSolid(centerX, feetY + 1);
    if (!hasGroundAhead) {
      const headClear =
        !terrain.isSolid(centerX, edgeY) && !terrain.isSolid(centerX, edgeY + this.height / 2);
      if (headClear) {
        if (otherWorms && this.collidesWithWorms(edgeX, edgeY, otherWorms)) {
          this.state = 'idle';
          return false;
        }
        this.position.x = edgeX;
        this.isGrounded = false;
        this.state = 'falling';
        // Walk forward off the ledge (see above) rather than dropping straight
        // down onto the edge corner.
        this.velocity.x = dx * WA_PHYSICS.TICK_RATE;
        return true;
      }
    }

    this.state = 'idle';
    return false;
  }

  private collidesWithWorms(x: number, y: number, otherWorms: Worm[]): boolean {
    const myBox = { x, y, width: this.width, height: this.height };

    for (const other of otherWorms) {
      if (other.id === this.id || other.state === 'dead') continue;

      const otherBox = other.getBounds();

      const overlapX =
        Math.min(myBox.x + myBox.width, otherBox.x + otherBox.width) -
        Math.max(myBox.x, otherBox.x);
      const overlapY =
        Math.min(myBox.y + myBox.height, otherBox.y + otherBox.height) -
        Math.max(myBox.y, otherBox.y);

      if (overlapX > 0 && overlapY > 0) {
        return true;
      }
    }
    return false;
  }

  stopWalking(): void {
    if (this.state === 'walking') {
      this.state = 'idle';
    }
  }

  jump(_terrain: CollisionMask): boolean {
    if (!this.isGrounded || this.state === 'dead') return false;

    const forwardSpeed = WA_PHYSICS.WORM.JUMP_FORWARD_SPEED;
    this.velocity.x = this.facing === 'right' ? forwardSpeed : -forwardSpeed;
    this.velocity.y = -WA_PHYSICS.WORM.JUMP_VELOCITY;
    this.isGrounded = false;
    this.state = 'jumping';
    return true;
  }

  backflip(_terrain: CollisionMask): boolean {
    if (!this.isGrounded || this.state === 'dead') return false;

    const dir = this.facing === 'left' ? 1 : -1;
    this.velocity.x = WA_PHYSICS.WORM.BACKFLIP_VELOCITY.x * dir;
    this.velocity.y = -WA_PHYSICS.WORM.BACKFLIP_VELOCITY.y;
    this.isGrounded = false;
    this.state = 'backflipping';
    return true;
  }

  setAimAngle(angle: number): void {
    // Aim always moves in whole degrees, clamped to the mode's range.
    this.aimAngle = Math.max(this.aimMin, Math.min(this.aimMax, Math.round(angle)));
  }

  adjustAim(delta: number): void {
    this.setAimAngle(this.aimAngle + delta);
  }

  /** World launch angle (degrees above horizon, in the worm's facing frame):
   *  the player's relative aim plus the ground tilt under the tank. On flat
   *  ground (or classic mode) this equals aimAngle. On a slope the whole span
   *  rotates so it leans up the hill when facing UPHILL and down when facing
   *  DOWNHILL (e.g. facing uphill on a 20deg slope, 10..80 -> 30..100). */
  getEffectiveAimAngle(): number {
    return this.aimAngle + this.groundTiltDeg;
  }

  /** Pure: the surface slope (degrees) under the worm, sampling the ground
   *  height left/right of its feet. POSITIVE when the ground rises to the right
   *  (height grows with x); FACING-INDEPENDENT. Returns 0 when the mode rule is
   *  off, (optionally) the worm is airborne, or no ground is found under the
   *  feet (cliff edge). Clamped to +/-75 to ignore near-vertical noise. */
  computeGroundSlopeDeg(
    terrain: { isSolid(x: number, y: number): boolean },
    requireGrounded = true,
  ): number {
    if (!this.aimRelativeToGround || (requireGrounded && !this.isGrounded)) {
      return 0;
    }
    const footX = Math.round(this.position.x + this.width / 2);
    const footY = Math.round(this.position.y + this.height);
    // First solid pixel y in a window around the feet = the ground surface at
    // that column (null if none, e.g. off a cliff / airborne).
    const groundYAt = (x: number): number | null => {
      for (let y = footY - 16; y <= footY + 16; y++) {
        if (terrain.isSolid(x, y)) return y;
      }
      return null;
    };
    const center = groundYAt(footX);
    if (center === null) {
      return 0;
    }
    // Average ground height a few px to each side (centre included).
    let leftSum = center;
    let leftN = 1;
    let rightSum = center;
    let rightN = 1;
    for (let k = 1; k <= 9; k++) {
      const l = groundYAt(footX - k);
      if (l !== null) {
        leftSum += l;
        leftN++;
      }
      const r = groundYAt(footX + k);
      if (r !== null) {
        rightSum += r;
        rightN++;
      }
    }
    // Rise-to-the-right: ground higher on the right (smaller y) => rightAvg <
    // leftAvg => positive.
    const worldSlope = (Math.atan2(leftSum / leftN - rightSum / rightN, 19) * 180) / Math.PI;
    return Math.max(-75, Math.min(75, worldSlope));
  }

  /** Recompute the slope/tilt under the worm. The barrel is rigidly attached to
   *  the chassis, so the aim span rotates with the slope: facing UPHILL tilts
   *  it UP (positive), facing DOWNHILL tilts it DOWN (negative). So the AIM tilt
   *  is facing-signed (groundTiltDeg = worldSlope * facingSign) while the raw
   *  slope (groundSlopeDeg, used for the sprite rotation) is facing-independent.
   *  Live while grounded, frozen at fire time. */
  updateGroundTilt(terrain: { isSolid(x: number, y: number): boolean }): void {
    const slope = this.computeGroundSlopeDeg(terrain, true);
    this.groundSlopeDeg = slope;
    const facingSign = this.facing === 'right' ? 1 : -1;
    this.groundTiltDeg = slope * facingSign;
  }

  getAimDirection(): Vector2 {
    const aim = this.getEffectiveAimAngle();
    const baseAngle = this.facing === 'right' ? aim : 180 - aim;
    const rad = (baseAngle * Math.PI) / 180;
    return {
      x: Math.cos(rad),
      y: -Math.sin(rad),
    };
  }

  addPendingDamage(amount: number): void {
    this.pendingDamage += amount;
  }

  clearPendingDamage(): void {
    this.pendingDamage = 0;
  }

  applyExplosionPush(pushX: number, pushY: number): void {
    this.velocity.x += pushX;
    this.velocity.y += pushY;
    this.isGrounded = false;

    if (Math.abs(pushX) > 10 || Math.abs(pushY) > 10) {
      this.isThrown = true;
    }
  }

  resolvePendingDamage(): { damage: number; died: boolean } {
    if (this.pendingDamage <= 0) {
      return { damage: 0, died: false };
    }

    const damage = this.pendingDamage;
    this.pendingDamage = 0;

    this.health = Math.max(0, this.health - damage);
    this.onDamage?.(damage);

    const died = this.health <= 0;
    if (died) {
      this.die();
    }

    return { damage, died };
  }

  hasPendingDamage(): boolean {
    return this.pendingDamage > 0;
  }

  takeDamage(amount: number, pushX: number, pushY: number): void {
    this.health = Math.max(0, this.health - amount);

    this.velocity.x += pushX;
    this.velocity.y += pushY;
    this.isGrounded = false;

    if (Math.abs(pushX) > 10 || Math.abs(pushY) > 10) {
      this.isThrown = true;
    }

    this.onDamage?.(amount);

    if (this.health <= 0) {
      this.die();
    }
  }

  startDrowning(): void {
    if (this.state === 'dead' || this.state === 'drowning') return;
    this.state = 'drowning';
    this.drowningTimer = 1.5;
    this.isGrounded = false;
    this.parachuteOpen = false;
  }

  die(): void {
    if (this.state === 'dead') return;
    const lastPosition = { x: this.position.x, y: this.position.y };
    this.state = 'dead';
    this.active = false;
    this.pendingDamage = 0;
    this.onDie?.(lastPosition);
  }

  applyTerrainPhysics(
    terrain: CollisionMask,
    platforms: Array<{ x: number; y: number; width: number; height: number }> = [],
  ): void {
    if (this.state === 'dead') return;

    const centerX = this.position.x + this.width / 2;
    const feetY = this.position.y + this.height;

    if (this.isEmbeddedInTerrain(terrain)) {
      this.escapeFromTerrain(terrain);
    }

    if (this.velocity.x !== 0) {
      const sideX = this.velocity.x > 0 ? this.position.x + this.width : this.position.x;
      const hitsSide =
        terrain.isSolid(sideX, this.position.y + 4) ||
        terrain.isSolid(sideX, this.position.y + this.height / 2) ||
        terrain.isSolid(sideX, this.position.y + this.height - 4);
      if (hitsSide) {
        let pushCount = 0;
        const maxPush = 50;
        while (
          pushCount < maxPush &&
          this.velocity.x > 0 &&
          terrain.isSolid(this.position.x + this.width, this.position.y + this.height / 2)
        ) {
          this.position.x -= 1;
          pushCount++;
        }
        pushCount = 0;
        while (
          pushCount < maxPush &&
          this.velocity.x < 0 &&
          terrain.isSolid(this.position.x, this.position.y + this.height / 2)
        ) {
          this.position.x += 1;
          pushCount++;
        }
        this.velocity.x = 0;
      }
    }

    if (this.velocity.y >= 0) {
      const groundY = this.findGroundBelow(centerX, feetY, terrain);
      const platformY = this.findPlatformBelow(centerX, feetY, platforms);

      let landOnY: number | null = null;
      if (groundY !== null && platformY !== null) {
        landOnY = Math.min(groundY, platformY);
      } else {
        landOnY = groundY ?? platformY;
      }

      if (landOnY !== null && landOnY - feetY <= 2) {
        const landingVelocity = this.velocity.y;
        this.position.y = landOnY - this.height;
        this.velocity.y = 0;
        this.isGrounded = true;

        if (this.state === 'jumping' || this.state === 'falling') {
          this.state = 'idle';
        }

        if (landingVelocity > WA_PHYSICS.WORM.FALL_DAMAGE_THRESHOLD && !this.immuneToFallDamage) {
          const vspeed = landingVelocity / WA_PHYSICS.TICK_RATE;
          const vspeedThreshold = WA_PHYSICS.WORM.FALL_DAMAGE_THRESHOLD / WA_PHYSICS.TICK_RATE;
          const fallDamage = Math.floor(
            ((vspeed - vspeedThreshold) * WA_PHYSICS.WORM.FALL_DAMAGE_COEF + 18) / 18,
          );

          if (fallDamage > 0) {
            this.addPendingDamage(fallDamage);
            this.onFallDamage?.(fallDamage);
          }
        }
        return;
      }
    }

    const leftFoot = this.position.x + 4;
    const rightFoot = this.position.x + this.width - 4;
    const hasTerrainBelow =
      terrain.isSolid(leftFoot, feetY + 1) ||
      terrain.isSolid(centerX, feetY + 1) ||
      terrain.isSolid(rightFoot, feetY + 1);
    const hasPlatformBelow = this.isOnPlatform(centerX, feetY + 1, platforms);

    if (!hasTerrainBelow && !hasPlatformBelow) {
      this.isGrounded = false;
      if (this.state !== 'jumping') {
        this.state = 'falling';
      }
    }
  }

  private findPlatformBelow(
    x: number,
    startY: number,
    platforms: Array<{ x: number; y: number; width: number; height: number }>,
  ): number | null {
    for (const plat of platforms) {
      if (x >= plat.x && x <= plat.x + plat.width) {
        const platTop = plat.y;
        if (platTop >= startY && platTop <= startY + 5) {
          return platTop;
        }
      }
    }
    return null;
  }

  private isOnPlatform(
    x: number,
    y: number,
    platforms: Array<{ x: number; y: number; width: number; height: number }>,
  ): boolean {
    for (const plat of platforms) {
      if (x >= plat.x && x <= plat.x + plat.width) {
        const platTop = plat.y;
        if (y >= platTop && y <= platTop + 3) {
          return true;
        }
      }
    }
    return false;
  }

  private findGroundBelow(x: number, startY: number, terrain: CollisionMask): number | null {
    for (let dy = 0; dy <= 5; dy++) {
      if (terrain.isSolid(x, startY + dy)) {
        return startY + dy;
      }
    }
    return null;
  }

  private collidesWithTerrain(
    x: number,
    y: number,
    terrain: CollisionMask,
    mode: 'full' | 'skipFeet' | 'narrow' = 'full',
  ): boolean {
    if (mode === 'narrow') {
      const hitboxPoints = [
        { x: x + 4, y: y },
        { x: x + this.width - 4, y: y },
        { x: x + 4, y: y + this.height / 2 },
        { x: x + this.width - 4, y: y + this.height / 2 },
      ];
      return hitboxPoints.some((p) => terrain.isSolid(p.x, p.y));
    }

    const hitboxPoints = [
      { x: x + 2, y: y },
      { x: x + this.width - 2, y: y },
      { x: x, y: y + this.height / 2 },
      { x: x + this.width, y: y + this.height / 2 },
    ];

    if (mode !== 'skipFeet') {
      hitboxPoints.push(
        { x: x + 2, y: y + this.height - 1 },
        { x: x + this.width - 2, y: y + this.height - 1 },
      );
    }

    return hitboxPoints.some((p) => terrain.isSolid(p.x, p.y));
  }

  private isEmbeddedInTerrain(terrain: CollisionMask): boolean {
    const checkPoints = [
      { x: this.position.x + 4, y: this.position.y + 2 },
      { x: this.position.x + this.width - 4, y: this.position.y + 2 },
      { x: this.position.x + 2, y: this.position.y + this.height / 2 },
      { x: this.position.x + this.width - 2, y: this.position.y + this.height / 2 },
      { x: this.position.x + this.width / 2, y: this.position.y + this.height / 2 },
    ];
    return checkPoints.some((p) => terrain.isSolid(p.x, p.y));
  }

  private escapeFromTerrain(terrain: CollisionMask): void {
    const startX = this.position.x;
    const startY = this.position.y;

    for (let dist = 1; dist <= 60; dist++) {
      for (let angle = 0; angle < 360; angle += 45) {
        const rad = (angle * Math.PI) / 180;
        const testX = startX + Math.cos(rad) * dist;
        const testY = startY + Math.sin(rad) * dist;

        if (!this.isEmbeddedInTerrain(terrain)) {
          return;
        }

        this.position.x = testX;
        this.position.y = testY;

        if (!this.isEmbeddedInTerrain(terrain)) {
          this.velocity.x = 0;
          this.velocity.y = 0;
          return;
        }
      }
    }

    this.position.x = startX;
    this.position.y = startY;
  }

  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.width,
      height: this.height,
    };
  }

  getCenterPosition(): Vector2 {
    return {
      x: this.position.x + this.width / 2,
      y: this.position.y + this.height / 2,
    };
  }

  destroy(): void {
    this.active = false;
  }
}
